import React, { useState, useEffect } from 'react';
import { Search } from 'react-feather';
import * as Icons from 'react-feather';
import { docsCategories, searchDocs } from './docsData';

const DocsHome = ({ onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger animations after mount
    const timer = setTimeout(() => setIsLoaded(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length > 1) {
      const results = searchDocs(query);
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleResultClick = (result) => {
    onNavigate(result.categorySlug, result.slug);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const getIcon = (iconName, size = 20) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={size} /> : <Icons.Book size={size} />;
  };

  return (
    <div className="docs-home" style={{
      padding: '80px 80px',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      {/* Hero - Centered */}
      <div className="docs-home-hero" style={{
        textAlign: 'center',
        marginBottom: '40px',
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
      }}>
        <h1 className="docs-home-title" style={{
          fontSize: '3.5rem',
          fontWeight: '700',
          color: '#1a1a1a',
          marginBottom: '20px',
          letterSpacing: '-1px',
          lineHeight: '1.1'
        }}>
          How can we help?
        </h1>
        <p className="docs-home-subtitle" style={{
          fontSize: '1.15rem',
          color: 'rgba(0, 0, 0, 0.5)',
          lineHeight: '1.6',
          maxWidth: '500px',
          margin: '0 auto'
        }}>
          Get answers to common questions on all things Lauft
        </p>
      </div>

      {/* Search - Separate from hero to fix z-index */}
      <div className="docs-home-search" style={{
        position: 'relative',
        maxWidth: '500px',
        margin: '0 auto 20px',
        zIndex: 100,
        textAlign: 'center',
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.6s ease-out 0.2s'
      }}>
        <Search
          size={18}
          style={{
            position: 'absolute',
            left: '18px',
            top: '16px',
            color: 'rgba(0, 0, 0, 0.35)'
          }}
        />
        <input
          type="text"
          placeholder="Search help (e.g. showings, claiming, or messaging)"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
          style={{
            width: '100%',
            padding: '16px 18px 16px 50px',
            fontSize: '0.95rem',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '10px',
            color: '#1a1a1a',
            outline: 'none',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            boxSizing: 'border-box'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
          }}
          onMouseLeave={(e) => {
            if (document.activeElement !== e.currentTarget) {
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
            }
          }}
        />

        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            maxHeight: '360px',
            overflowY: 'auto',
            zIndex: 9999,
            textAlign: 'left',
            animation: 'dropdownFadeIn 0.2s ease-out'
          }}>
            {searchResults.slice(0, 8).map((result, index) => (
              <button
                key={`${result.categorySlug}-${result.slug}`}
                onClick={() => handleResultClick(result)}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: index < Math.min(searchResults.length, 8) - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.03)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  color: '#1a1a1a',
                  marginBottom: '4px'
                }}>
                  {result.title}
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  color: 'rgba(0, 0, 0, 0.45)'
                }}>
                  {result.categoryTitle} • {result.summary?.slice(0, 60)}...
                </div>
              </button>
            ))}
          </div>
        )}

        {/* No results message */}
        {showResults && searchQuery.trim().length > 1 && searchResults.length === 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            padding: '24px 20px',
            textAlign: 'center',
            zIndex: 9999,
            animation: 'dropdownFadeIn 0.2s ease-out'
          }}>
            <div style={{
              fontSize: '0.95rem',
              color: 'rgba(0, 0, 0, 0.5)'
            }}>
              No results found for "{searchQuery}"
            </div>
          </div>
        )}
      </div>

      {/* Popular searches */}
      <div className="docs-home-popular" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '80px',
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.6s ease-out 0.3s'
      }}>
        <span style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.45)' }}>
          Popular searches:
        </span>
        {['claiming', 'showings', 'messaging'].map(term => (
          <button
            key={term}
            onClick={() => handleSearch(term)}
            style={{
              padding: '6px 14px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: 'rgba(0, 0, 0, 0.7)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)';
              e.currentTarget.style.color = '#1a1a1a';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
              e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {term}
          </button>
        ))}
      </div>

      {/* Categories Grid */}
      {docsCategories.map((category, categoryIndex) => (
        <div
          key={category.slug}
          style={{
            marginBottom: '60px',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(30px)',
            transition: `opacity 0.6s ease-out ${0.4 + categoryIndex * 0.1}s, transform 0.6s ease-out ${0.4 + categoryIndex * 0.1}s`,
            position: 'relative',
            zIndex: 1
          }}
        >
          {/* Category Header */}
          <h2 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#1a1a1a',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ color: 'rgba(0, 0, 0, 0.4)' }}>
              {getIcon(category.icon, 18)}
            </span>
            {category.title}
          </h2>

          {/* Articles Grid */}
          <div className="docs-home-articles-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '16px'
          }}>
            {category.articles.map((article, articleIndex) => (
              <div
                key={article.slug}
                onClick={() => onNavigate(category.slug, article.slug)}
                style={{
                  backgroundColor: '#fafafa',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  borderRadius: '12px',
                  padding: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h3 style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  marginBottom: '8px',
                  lineHeight: '1.4'
                }}>
                  {article.title}
                </h3>
                <p style={{
                  fontSize: '0.85rem',
                  color: 'rgba(0, 0, 0, 0.5)',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {article.summary}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* CSS Keyframes + mobile overrides */}
      <style>{`
        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .docs-home {
            padding: 32px 20px !important;
          }
          .docs-home-hero {
            margin-bottom: 28px !important;
          }
          .docs-home-title {
            font-size: 2rem !important;
            margin-bottom: 12px !important;
            letter-spacing: -0.5px !important;
          }
          .docs-home-subtitle {
            font-size: 1rem !important;
          }
          .docs-home-popular {
            margin-bottom: 40px !important;
            gap: 8px !important;
          }
          .docs-home-articles-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DocsHome;